import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import UserGuide from "./UserGuide";
import HelpContent from "./HelpContent";
import DefinitionContent from "./DefinitionContent";
import { setShowHelpSection } from "../../slices/helpContentSlice";
import GTM from "../../GTM.js";
import PropTypes from "prop-types";

const getUserGuideVideo = (helpContentSection = []) =>
    helpContentSection.find((section) =>
        section.Name.toLowerCase().includes("video")
    );

const hasUserGuideContent = (userGuideVideo) =>
    Array.isArray(userGuideVideo?.Content) && userGuideVideo.Content.length > 0;

function HelpSection({ showModal, closeHelp }) {
    const dispatch = useDispatch();
    const helpContentSection = useSelector(
        (state) => state?.helpContent?.helpContentSection
    );
    const frequentlyAskedQuestions = useSelector(
        (state) => state?.helpContent?.frequentlyAskedQuestions
    );
    const definitionsExplained = useSelector(
        (state) => state?.helpContent?.definitionsExplained
    );
    const showHelpSection = useSelector(
        (state) => state?.helpContent?.showHelpSection
    );
    const shouldOpenUserGuide = Boolean(showHelpSection);
    const initialUserGuideVideo = shouldOpenUserGuide
        ? getUserGuideVideo(helpContentSection)
        : null;
    const [show, setShow] = useState(showModal && !shouldOpenUserGuide);
    const [showUserGuide, setShowUserGuide] = useState(
        shouldOpenUserGuide && hasUserGuideContent(initialUserGuideVideo)
    );
    const [showHelpContent, setShowHelpContent] = useState(false);
    const [activeAccordion, setActiveAccordion] = useState(null);
    const [selectedHelpContent, setSelectedHelpContent] = useState(null);
    const [userGuideVideo, setUserGuideVideo] = useState(initialUserGuideVideo);
    const [animationClass, setAnimationClass] = useState(
        showModal && !shouldOpenUserGuide ? "slide-in" : ""
    );
    const [showDefinitionContent, setShowDefinitionContent] = useState(false);
    const [selectedDefinitionContent, setSelectedDefinitionContent] =
        useState(null);
    const gtm = new GTM(null);
    useEffect(() => {
        if (shouldOpenUserGuide) {
            return;
        }

        if (showModal) {
            setShow(true);
            setAnimationClass("slide-in");
            gtmEventOnVisible();
        } else {
            setAnimationClass("slide-out");
            setTimeout(() => setShow(false), 500);
        }
    }, [showModal, shouldOpenUserGuide]);

    const handleClose = () => {
        setAnimationClass("slide-out");
        setTimeout(() => {
            setShow(false);
            closeHelp();
            dispatch(setShowHelpSection(false));
        }, 900);
    };

    function gtmEventOnVisible() {
        gtm.Event(
            "help section",
            "visibility",
            "visible",
            "help panel",
            "help panel",
            "undefined",
            "help_panel_visibility",
            "undefined"
        );
    }

    useEffect(() => {
        if (showHelpSection) {
            const selectedContent = getUserGuideVideo(helpContentSection);
            if (!hasUserGuideContent(selectedContent)) return;

            setUserGuideVideo(selectedContent);
            setShow(false);
            setShowUserGuide(true);
            setShowHelpContent(false);
        }
    }, [showHelpSection, helpContentSection]);

    const handleHelpCard = (helpCardName) => {
        const selectedContent = helpContentSection.find(
            (section) => section.Name === helpCardName
        );
        if (helpCardName.toLowerCase().includes("video")) {
            setUserGuideVideo(selectedContent);
            setShowUserGuide(true);
            setShowHelpContent(false);
        } else if (selectedContent) {
            setSelectedHelpContent(selectedContent);
            setShowHelpContent(true);
        }
        gtm.Event(
            "help section",
            "button",
            "click",
            helpCardName,
            "help",
            "help",
            "help_item_click",
            ""
        );
    };

    const handleDefinitionCard = (helpCardName) => {
        const selectedContent = definitionsExplained.find(
            (section) => section.Name === helpCardName
        );
        if (selectedContent) {
            setSelectedDefinitionContent(selectedContent);
            setShowDefinitionContent(true);
        }
        gtm.Event(
            "help section",
            "button",
            "click",
            helpCardName,
            "help",
            "definitions explained",
            "navigation_click",
            ""
        );
    };

    const handleAccordionToggle = useCallback(
        (index, faq) => {
            const isExpanding = index !== activeAccordion;
            setActiveAccordion((prevState) =>
                index === prevState ? null : index
            );
            if (isExpanding) {
                gtm.Event(
                    "custom_event",
                    "accordion",
                    "expand",
                    faq.Header,
                    "help",
                    "faqs",
                    "faq_expand",
                    "undefined"
                );
            }
        },
        [activeAccordion]
    );

    const handleBack = () => {
        setShowUserGuide(false);
        setShowHelpContent(false);
        setShowDefinitionContent(false);
        dispatch(setShowHelpSection(false));
        setShow(true);
    };

    // Keydown event handler for scrolling
    const handleKeyDown = (event) => {
        const modalBody = document.querySelector(".modal-body");
        if (modalBody) {
            if (event.key === "ArrowDown") {
                event.preventDefault();
                modalBody.scrollBy({ top: 50, behavior: "smooth" });
            } else if (event.key === "ArrowUp") {
                event.preventDefault();
                modalBody.scrollBy({ top: -50, behavior: "smooth" });
            }
        }
    };

    useEffect(() => {
        if (show) {
            window.addEventListener("keydown", handleKeyDown);
        }
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [show]);

    const helpSectionProps = {
        showUserGuide,
        setShowUserGuide,
        closeHelp,
        handleBack,
        userGuideVideo,
        content: selectedHelpContent,
        showHelpContent,
        setShowHelpContent,
        selectedDefinitionContent,
        showDefinitionContent,
        setShowDefinitionContent,
        handleHelpClose: handleClose,
    };

    return (
        <>
            {showUserGuide && <UserGuide helpSectionProps={helpSectionProps} />}
            {showHelpContent && selectedHelpContent && (
                <HelpContent helpSectionProps={helpSectionProps} />
            )}
            {showDefinitionContent && selectedDefinitionContent && (
                <DefinitionContent helpSectionProps={helpSectionProps} />
            )}
            <Modal
                show={show}
                dialogClassName="help-section"
                size="xl"
                backdrop="true"
                onHide={handleClose}
                className={animationClass}
                backdropClassName="helpSection-backdrop"
            >
                <div className="help-section-container">
                    <Modal.Header style={{ justifyContent: "end" }}>
                        <button
                            className="help-buttons align-items-center border-0 bg-transparent"
                            onClick={handleClose}
                        >
                            <i className="fa-solid fa-xmark"></i> Close
                        </button>
                    </Modal.Header>
                    <Modal.Body
                        style={{
                            padding: 0,
                            maxHeight: "calc(100vh - 120px)",
                            overflowY: "auto",
                        }}
                    >
                        <div className="d-flex flex-direction">
                            <div>
                                <div className="header pb-3 pt-20p">Help</div>
                                <div className="help-card-container">
                                    {helpContentSection
                                        ?.slice()
                                        .sort(
                                            (a, b) =>
                                                (Number(a.Order) || 1) -
                                                (Number(b.Order) || 1)
                                        )
                                        .map((section, index) => {
                                            const order =
                                                Number(section.Order) || 1;
                                            const isFullWidth = order === 1;
                                            return (
                                                <div
                                                    key={section.Order}
                                                    className={`help-content ${
                                                        isFullWidth
                                                            ? "helpSection-card"
                                                            : "help-card"
                                                    }`}
                                                    onClick={() =>
                                                        handleHelpCard(
                                                            section.Name
                                                        )
                                                    }
                                                    style={{
                                                        gridColumn: isFullWidth
                                                            ? "span 2"
                                                            : "span 1",
                                                    }}
                                                >
                                                    <div
                                                        className="icons"
                                                        dangerouslySetInnerHTML={{
                                                            __html: section.IconHtml,
                                                        }}
                                                        style={{
                                                            width: 59,
                                                            height: 59,
                                                        }}
                                                    />
                                                    <div className="card-header">
                                                        {section?.Name}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </div>
                            </div>
                            <div>
                                <div
                                    className="header pt-3"
                                    style={{ marginTop: 68 }}
                                >
                                    Definitions Explained
                                </div>
                                <div className="help-card-container d-flex flex-direction">
                                    {definitionsExplained
                                        ?.slice()
                                        .sort(
                                            (a, b) =>
                                                (Number(a.Order) || 0) -
                                                (Number(b.Order) || 0)
                                        )
                                        .map((section) => (
                                            <div
                                                key={section.Order}
                                                className="section-card definitionsExplained"
                                                onClick={() =>
                                                    handleDefinitionCard(
                                                        section.Name
                                                    )
                                                }
                                            >
                                                <div
                                                    className="icons"
                                                    dangerouslySetInnerHTML={{
                                                        __html: section.IconHtml,
                                                    }}
                                                    style={{
                                                        width: 59,
                                                        height: 59,
                                                    }}
                                                />
                                                <div className="card-header">
                                                    {section?.Name}
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </div>
                            <div>
                                <div
                                    className="header pt-3"
                                    style={{ marginTop: 68 }}
                                >
                                    FAQs
                                </div>
                                <div style={{ width: 579 }}>
                                    {frequentlyAskedQuestions &&
                                    frequentlyAskedQuestions.length > 0 ? (
                                        frequentlyAskedQuestions.map(
                                            (faq, index) => (
                                                <div
                                                    className="accordion"
                                                    key={faq?.Header}
                                                >
                                                    <div
                                                        className="d-flex px-20p faq-subtext cursor-pointer"
                                                        onClick={() =>
                                                            handleAccordionToggle(
                                                                index,
                                                                faq
                                                            )
                                                        }
                                                    >
                                                        <h2 className="section-heading mb-0p">
                                                            <div className="faq-header">
                                                                {faq.Header}
                                                            </div>
                                                        </h2>
                                                        <i
                                                            className={`fa-solid fa-${activeAccordion === index ? "minus" : "plus"} accordion-icon ml-8p`}
                                                            tabIndex="0"
                                                        />
                                                    </div>
                                                    {activeAccordion ===
                                                        index && (
                                                        <div className="faq-subtext-collapse accordion-collapse show">
                                                            <div className="faq-card card-body">
                                                                {typeof faq.Description ===
                                                                "string" ? (
                                                                    faq.Description?.split(
                                                                        "\\n"
                                                                    ).map(
                                                                        (
                                                                            line,
                                                                            index
                                                                        ) => (
                                                                            <React.Fragment
                                                                                key={
                                                                                    index
                                                                                }
                                                                            >
                                                                                {
                                                                                    line
                                                                                }
                                                                                <br />
                                                                            </React.Fragment>
                                                                        )
                                                                    )
                                                                ) : (
                                                                    <ul className="faq-ul">
                                                                        {faq.Description?.Points?.map(
                                                                            (
                                                                                point,
                                                                                pointIndex
                                                                            ) => (
                                                                                <li
                                                                                    key={
                                                                                        pointIndex
                                                                                    }
                                                                                    className="faq-list"
                                                                                >
                                                                                    {
                                                                                        point
                                                                                    }
                                                                                </li>
                                                                            )
                                                                        )}
                                                                    </ul>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                    <hr className="mb-20p mx-20p faq-divider" />
                                                </div>
                                            )
                                        )
                                    ) : (
                                        <p>
                                            No frequently asked questions
                                            available.
                                        </p> // You can customize this message as needed
                                    )}
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                </div>
            </Modal>
        </>
    );
}

HelpSection.propTypes = {
    showModal: PropTypes.bool,
    closeHelp: PropTypes.func,
};

export default HelpSection;
